import { useEffect, useState } from 'react';
import { modules as bundledModules } from './modules';
import { fetchModuleCatalog } from '../services/api';

/**
 * Fiyatın TEK KAYNAĞI backend'deki `module_catalog` (C11).
 *
 * ─── NEDEN İKİ KATMAN ──────────────────────────────────────────────────────
 * `modules.js` modülün ANLATIMINI taşıyor (ad, ikon, özellik listesi, kime
 * uygun); fiyat, deneme süresi ve satılabilirlik `GET /public/module-catalog`
 * ucundan geliyor. Fiyatı da pakete gömseydik panelden yapılan zam vitrine
 * hiç yansımaz, müşteri 299₺ görüp 349₺ faturayla karşılaşırdı — ve hiçbir
 * yerde hata çıkmazdı.
 *
 * ─── PAKETTEKİ DEĞER SİLİNMİYOR, BAŞLANGIÇ DEĞERİ OLUYOR ───────────────────
 * İlk çizimde pakettekiler kullanılıyor. Ağ beklenip boş ekran gösterilseydi
 * fiyat bölümü her açılışta zıplar, arama motoru da fiyatsız bir sayfa
 * görürdü. API cevap veremezse (kesinti, engellenen istek) vitrin bayat ama
 * ÇALIŞIR kalıyor — fiyatsız kalmaktan iyidir.
 *
 * ─── KATALOGDA OLMAYAN MODÜL VİTRİNDE DE YOK ───────────────────────────────
 * Uç yalnızca satışa AÇIK modülleri döndürüyor. Katalogda görünmeyen modül
 * `purchasable: false` işaretleniyor, yani fiyat bölümünden düşüyor: fiyatı
 * gösterilen her modülün gerçekten satın alınabilmesi gerekiyor.
 *
 * ⚠️ TERSİ OTOMATİK DEĞİL: panelden YENİ bir modül eklenirse burada adı,
 * ikonu ve özellik listesi olmadığı için vitrinde çıkmaz. Yeni modül
 * `modules.js`e de yazılmalı — vitrin metni ticari veriden üretilemiyor.
 */

/** Tek uçuş: bileşenlerin hepsi aynı isteği paylaşıyor. */
let inFlight = null;

const loadCatalog = () => {
  if (!inFlight) {
    inFlight = fetchModuleCatalog().catch((error) => {
      // Bir sonraki denemede yeniden istensin diye önbellek temizleniyor.
      inFlight = null;
      throw error;
    });
  }
  return inFlight;
};

/**
 * "Aylık" rozetini FİYATTAN türetir.
 *
 * ⚠️ Rozet `modules.js`te düz metin olarak yazılıydı ve katalog yalnızca
 * `price` alanını eziyordu: fiyat değişince detay sayfası kartta yeni,
 * rozette ESKİ fiyatı gösterirdi — aynı sayfada iki fiyat, hiçbir yerde hata
 * yok. SSS cümlesiyle aynı ders (§ vitrin).
 */
const withPriceHighlight = (module) => {
  if (!Array.isArray(module.highlights) || !module.price) return module;
  const monthly = Number(module.price.monthly) || 0;
  return {
    ...module,
    highlights: module.highlights.map((highlight) =>
      /^Aylık/.test(highlight.label)
        ? { ...highlight, value: `${monthly.toLocaleString('tr-TR')} ₺` }
        : highlight
    )
  };
};

/** Katalog satırlarını paketteki anlatımın üzerine bindirir. */
const mergeCatalog = (rows) => {
  const byName = new Map(rows.map((row) => [row.moduleName, row]));

  return bundledModules.map((module) => {
    const row = byName.get(module.moduleName);

    // Katalogda yoksa satışa kapalı demektir (uç yalnızca açık olanları döner).
    if (!row) return withPriceHighlight({ ...module, purchasable: false });

    return withPriceHighlight({
      ...module,
      purchasable: true,
      price: {
        monthly: Number(row.prices?.monthly) || 0,
        yearly: Number(row.prices?.yearly) || 0
      },
      trialDays: row.freeDays ?? null,
      vatIncluded: row.vatIncluded === true
    });
  });
};

const initialModules = bundledModules.map(withPriceHighlight);

/**
 * @returns {{ modules: Array, purchasableModules: Array, vatIncluded: boolean, source: 'bundle'|'catalog' }}
 */
export const useModuleCatalog = () => {
  const [modules, setModules] = useState(initialModules);
  const [source, setSource] = useState('bundle');

  useEffect(() => {
    let active = true;

    loadCatalog()
      .then((rows) => {
        if (!active || !Array.isArray(rows) || rows.length === 0) return;
        setModules(mergeCatalog(rows));
        setSource('catalog');
      })
      .catch(() => {
        // Sessiz: pakettekiler zaten ekranda. Fiyat okunamadı diye ziyaretçiye
        // hata göstermek, çalışan bir sayfayı bozmak olurdu.
      });

    return () => {
      active = false;
    };
  }, []);

  const purchasableModules = modules.filter((module) => module.purchasable);

  // Modül başına ayarlanabiliyor ama vitrin tek cümle yazıyor: hepsi KDV
  // dahil değilse "dahil değildir" denir. Karışık durumda güvenli taraf da bu.
  const vatIncluded = purchasableModules.length > 0
    && purchasableModules.every((module) => module.vatIncluded === true);

  return { modules, purchasableModules, vatIncluded, source };
};

export default useModuleCatalog;
